import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Calendar, User, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { ssgData } from "@/lib/ssg";
import useSEO from "@/hooks/useSEO";

/**
 * ContentDetailPage — shared detail page for Case Reports & Articles.
 */
export default function ContentDetailPage({
  routeBase,     // "/case-reports" | "/articles"
  backLabel,     // "Back to case reports" | "Back to articles"
  testIdPrefix,  // "case" | "article"
}) {
  const { slug } = useParams();
  const [item, setItem] = useState(() => ssgData(`contentItem:${slug}`) || null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setItem(ssgData(`contentItem:${slug}`) || null);
    setNotFound(false);
    api
      .get(`/content/${slug}`)
      .then(({ data }) => setItem(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useSEO({
    title: item?.title || "Loading",
    description: item?.excerpt || "",
    image: item?.cover_image,
    type: "article",
  });

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid={`${testIdPrefix}-not-found`}>
        <h1 className="font-display text-4xl text-[#2D2623]">Not found</h1>
        <Link to={routeBase} className="mt-6 inline-flex items-center gap-2 text-[#E07A5F]">
          <ArrowLeft className="w-4 h-4" /> {backLabel}
        </Link>
      </div>
    );
  }

  if (!item) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-[#A39171]">Loading…</div>;
  }

  return (
    <div data-testid={`${testIdPrefix}-detail-page`}>
      <section className="max-w-3xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-10">
        <Link
          to={routeBase}
          data-testid={`${testIdPrefix}-back`}
          className="inline-flex items-center gap-2 text-[#5C524E] hover:text-[#E07A5F] text-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> {backLabel}
        </Link>
        {item.tag && <p className="eyebrow text-[#A39171] mt-8 mb-5">{item.tag}</p>}
        <h1 className="font-display text-4xl md:text-6xl text-[#2D2623] leading-[1.05] tracking-tight">
          {item.title}
        </h1>
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#5C524E]">
          <span className="flex items-center gap-2">
            <User className="w-4 h-4 text-[#E07A5F]" /> {item.author}
          </span>
          <span className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#E07A5F]" />
            {new Date(item.created_at).toLocaleDateString("en-IN", { month: "long", day: "numeric", year: "numeric" })}
          </span>
          {item.external_url && (
            <a
              href={item.external_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testIdPrefix}-external-link`}
              className="inline-flex items-center gap-1.5 text-[#E07A5F] hover:underline"
            >
              Open publication <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </section>

      {item.cover_image && (
        <section className="max-w-5xl mx-auto px-6 md:px-10 mb-12">
          <div className="rounded-3xl overflow-hidden aspect-[16/9]">
            <img src={item.cover_image} alt={item.title} className="w-full h-full object-cover" />
          </div>
        </section>
      )}

      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <p className="text-xl text-[#2D2623] font-display leading-snug border-l-2 border-[#E07A5F] pl-5 italic">
          {item.excerpt}
        </p>
        <div className="mt-10 text-[#2D2623]/90 text-lg leading-[1.85] whitespace-pre-line">
          {item.content}
        </div>

        {item.external_url && (
          <a
            href={item.external_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testIdPrefix}-cta-external`}
            className="mt-12 inline-flex items-center gap-2 px-7 h-12 rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] transition-all font-medium"
          >
            View full publication <ArrowUpRight className="w-4 h-4" />
          </a>
        )}
      </section>
    </div>
  );
}
